import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { downgradeToAJSService} from 'ib-web-commons/utils';

@Injectable({
    providedIn: 'root'
})
export class GiftCardsGenericProxyService {

    constructor (private http: HttpClient) { }

    private request<T>(
        method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
        url: string,
        options: {
            body?: any,
            type?: string,
            maxTimeout?: number,
            newConfig?: { headers?: { [header: string]: string | string[] } }
        } = {}
    ): Observable<{ data: any; code: number } | CommonsErrorsModel.ErrorDataGeneral> {
        let headers = options.newConfig?.headers ? new HttpHeaders(options.newConfig.headers) : undefined;
        if (options.type === 'dict') {
            headers = headers ? headers.set('Accept-Language', (window as any).appLang || 'es') : new HttpHeaders({ 'Accept-Language': (window as any).appLang || 'es' });
        }
        if (method === 'POST' && options.body && (options.body as any)['person']) {
            headers = new HttpHeaders({ 'transaction-channel': (options.body as any)['person'].sourceName });
        }
        const httpOptions: any = { headers, observe: 'response' as const };
        if (options.maxTimeout) httpOptions.timeout = options.maxTimeout;

        let req$: Observable<any>;
        switch (method) {
                case 'GET':
                    req$ = this.http.get(url, httpOptions);
                    break;
                case 'POST':
                    req$ = this.http.post(url, options.body ? JSON.stringify(options.body) : null, httpOptions);
                    break;
                case 'PUT':
                    req$ = this.http.put(url, options.body ? JSON.stringify(options.body) : null, httpOptions);
                    break;
                case 'DELETE':
                    req$ = this.http.delete(url, httpOptions);
                    break;
                case 'PATCH':
                    req$ = this.http.patch(url, options.body ? JSON.stringify(options.body) : null, httpOptions);
                    break;
                default:
                    throw new Error('Unsupported HTTP method');
        }

        return req$.pipe(
            map((response: any) => ({ data: response.body, code: response.status })),
            catchError((error: any) => {
                const errorDataGeneral: CommonsErrorsModel.ErrorDataGeneral = {
                    errorCode: error.status,
                    requestURL: error.url || (error.config && error.config.url) || url,
                    config: error.config,
                    data: undefined
                };
                if (error.error) {
                    if (typeof error.error === 'object' && (error.error.reason || error.error.code)) {
                        errorDataGeneral.data = {
                            error: {
                                reason: error.error.reason,
                                code: error.error.code,
                                errorDescription: error.error.errorDescription,
                                errorCode: error.error.errorCode
                            }
                        };
                    } else if (typeof error.error === 'string') {
                        errorDataGeneral.data = {
                            error: {
                                reason: error.error,
                                code: ''
                            }
                        };
                    }
                }
                return of(errorDataGeneral);
            })
        );
    }

    getGeneric (
        url: string,
        type?: string,
        maxTimeout?: number,
        newConfig?: { headers?: { [header: string]: string | string[] } }
    ): Observable<{ data: any; code: number } | CommonsErrorsModel.ErrorDataGeneral> {
        return this.request('GET', url, { type, maxTimeout, newConfig });
    }

    postGeneric (
        url: string,
        data: object,
        newConfig?: { headers?: { [header: string]: string | string[] } },
        maxTimeout?: number
    ): Observable<{ data: any; code: number } | CommonsErrorsModel.ErrorDataGeneral> {
        return this.request('POST', url, { body: data, newConfig, maxTimeout });
    }

    putGeneric (
        url: string,
        data: object,
        newConfig?: { headers?: { [header: string]: string | string[] } },
        maxTimeout?: number
    ): Observable<{ data: any; code: number } | CommonsErrorsModel.ErrorDataGeneral> {
        return this.request('PUT', url, { body: data, newConfig, maxTimeout });
    }

    deleteGeneric (
        url: string,
        newConfig?: { headers?: { [header: string]: string | string[] } },
        maxTimeout?: number
    ): Observable<{ data: any; code: number } | CommonsErrorsModel.ErrorDataGeneral> {
        return this.request('DELETE', url, { newConfig, maxTimeout });
    }

    patchGeneric (
        url: string,
        data: any,
        maxTimeout?: number
    ): Observable<{ data: any; code: number } | CommonsErrorsModel.ErrorDataGeneral> {
        return this.request('PATCH', url, { body: data, maxTimeout });
    }
}

downgradeToAJSService('GiftCardsGenericProxyService', GiftCardsGenericProxyService, 'ib-gift-cards-service-module');
